// partners/pages/F4_PartnerStatusPage.jsx
// Shown to authenticated users who are NOT yet approved partners.
// Reads /api/partners/status/ and renders state-appropriate UI.
// Auto-redirects approved + active users to /partners/dashboard.

import { useEffect, useRef } from "react";
import { useGetPartnerStatusQuery } from "../../../apis/apiSlice";
import { capturePartnerEvent } from "../../../utils/analytics";

const STATUS_CONFIG = {
  pending: {
    icon: "⏳",
    title: "Application Under Review",
    subtitle: "We've received your application and are reviewing it.",
    body:
      "Our team typically reviews applications within 48 hours. You'll receive an email the moment a decision is made.",
    color: "text-yellow-400",
    borderColor: "border-yellow-400/20",
  },
  under_review: {
    icon: "🔍",
    title: "Active Review In Progress",
    subtitle: "Your application is being reviewed right now.",
    body:
      "A member of our partnerships team is evaluating your application. You'll hear from us very soon.",
    color: "text-mnf-cyan",
    borderColor: "border-mnf-cyan/20",
  },
  waitlisted: {
    icon: "📋",
    title: "You're on the Waitlist",
    subtitle: "Your application is promising — we just need a bit more capacity.",
    body:
      "We're opening new partner slots regularly. We'll email you as soon as a spot opens up for your track.",
    color: "text-mnf-purple",
    borderColor: "border-mnf-purple/20",
  },
  rejected: {
    icon: "❌",
    title: "Application Not Approved",
    subtitle: "Unfortunately we couldn't approve your application at this time.",
    body:
      "This doesn't mean forever. You're welcome to reapply in 90 days. If you have questions, email partnerships@mnf.ai.",
    color: "text-mnf-error",
    borderColor: "border-mnf-error/20",
  },
  not_applied: {
    icon: "🚀",
    title: "You Haven't Applied Yet",
    subtitle: "Join the MNF Partner Program to start earning commissions.",
    body:
      "Choose your track — Film Ecosystem Partner or Creative Ambassador — and submit your application.",
    color: "text-mnf-cyan",
    borderColor: "border-mnf-cyan/20",
  },
};

export default function PartnerStatusPage() {
  const { data, isLoading, error } = useGetPartnerStatusQuery();
  const viewedRef = useRef(false);
  const redirectTrackedRef = useRef(false);

  useEffect(() => {
    if (isLoading || viewedRef.current || error) return;
    if (data?.status === "approved" && data?.is_active) {
      if (!redirectTrackedRef.current) {
        redirectTrackedRef.current = true;
        capturePartnerEvent("partner_status_redirect_dashboard");
      }
      return;
    }
    viewedRef.current = true;
    capturePartnerEvent("partner_status_viewed", {
      status: data?.status || "not_applied",
      partner_type: data?.partner_type,
    });
  }, [isLoading, data, error]);

  // Approved + active → send straight to dashboard
  if (!isLoading && data?.status === "approved" && data?.is_active) {
    window.location.replace("/partners/dashboard");
    return null;
  }

  // Loading spinner
  if (isLoading) {
    return (
      <div className="bg-mnf-dark-1 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-mnf-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in
  if (error?.status === 401 || error?.status === 403) {
    return (
      <div className="bg-mnf-dark-1 min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="font-poppins text-xl font-bold mb-2 text-mnf-text-primary">
            Please log in
          </h2>
          <p className="text-mnf-text-muted text-sm mb-6">
            You need to be logged in to check your partner status.
          </p>
          <a
            href="/login"
            className="inline-flex items-center gap-2 font-poppins font-semibold text-sm px-6 py-3 rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text"
          >
            Log in →
          </a>
        </div>
      </div>
    );
  }

  const statusKey = data?.status || "not_applied";
  const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG["not_applied"];

  return (
    <div className="bg-mnf-dark-1 min-h-screen font-inter text-mnf-text-primary flex flex-col items-center justify-center px-6 py-16">
      {/* Logo */}
      <div className="mb-10">
        <img
          src="https://cdn.prod.website-files.com/6890527cc3ef5b2eae4f1b43/68f32a89189c53cd269b54de_MNF_beta_light.svg"
          alt="MyNextFilm"
          className="h-8 mx-auto"
        />
      </div>

      {/* Status card */}
      <div
        className={`w-full max-w-md bg-mnf-dark-2 border ${cfg.borderColor} rounded-mnf-card p-8 text-center`}
      >
        <div className="text-5xl mb-5">{cfg.icon}</div>

        <h1 className={`font-poppins text-xl font-bold mb-2 ${cfg.color}`}>
          {cfg.title}
        </h1>
        <p className="font-poppins text-sm text-mnf-text-secondary mb-4">
          {cfg.subtitle}
        </p>
        <p className="text-sm text-mnf-text-muted leading-relaxed mb-6">
          {cfg.body}
        </p>

        {/* Reviewer note — shown for rejected / waitlisted */}
        {data?.review_notes && (
          <div className="bg-mnf-dark-3 border border-white/[0.06] rounded-mnf p-4 mb-6 text-left">
            <div className="text-xs font-poppins font-semibold text-mnf-text-muted uppercase tracking-wider mb-1">
              Reviewer note
            </div>
            <div className="text-sm text-mnf-text-secondary">{data.review_notes}</div>
          </div>
        )}

        {/* Submission metadata */}
        {data?.submitted_at && (
          <div className="text-xs text-mnf-text-muted mb-6">
            Submitted{" "}
            {new Date(data.submitted_at).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
            {data?.partner_type && ` · ${data.partner_type}`}
          </div>
        )}

        {/* CTA — context-sensitive */}
        {statusKey === "not_applied" && (
          <a
            href="/become-a-partner"
            onClick={() => capturePartnerEvent("partner_status_apply_clicked")}
            className="inline-flex items-center gap-2 font-poppins font-semibold text-sm px-8 py-3.5 rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text hover:-translate-y-0.5 transition-transform"
          >
            Apply now →
          </a>
        )}

        {statusKey === "rejected" && (
          <a
            href="mailto:partnerships@mnf.ai"
            className="inline-flex items-center gap-2 font-poppins font-semibold text-sm px-6 py-3 rounded-mnf-pill border border-white/20 text-mnf-text-secondary hover:border-mnf-cyan/40 transition-colors"
          >
            Contact partnerships team →
          </a>
        )}

        {(statusKey === "pending" ||
          statusKey === "under_review" ||
          statusKey === "waitlisted") && (
          <button
            onClick={() => {
              capturePartnerEvent("partner_status_refresh_clicked", { status: statusKey });
              window.location.reload();
            }}
            className="text-xs text-mnf-text-muted hover:text-mnf-cyan transition-colors"
          >
            Refresh status
          </button>
        )}
      </div>

      {/* Back link */}
      <div className="mt-6">
        <a
          href="/become-a-partner"
          className="text-xs text-mnf-text-muted hover:text-mnf-cyan transition-colors"
        >
          ← Back to Partner Program
        </a>
      </div>
    </div>
  );
}
